/**
 * 抽獎流程五段（選號、已送出未回應的號碼、報價、開獎結果揭示步序、金流回程終局）
 * 的狀態機。純函式、不碰 DOM、不碰任何全域——盤面資料（`boxes`）由呼叫端在每個
 * 事件裡帶入最新值，本模組不持有跨呼叫的盤面副本，只回傳選號的推導結果與（若該
 * 事件涉及鎖號／開獎）更新後的盤面，交呼叫端寫回自己的模組變數。
 *
 * 副作用一律回在 `effects` 陣列裡，呼叫端自行執行：
 *   { type: 'render', name, args }  DOM 渲染或既有輔助函式呼叫
 *   { type: 'redirect', url }       導頁
 */
import {
    toggleSelection,
    selectAllAvailable,
    pickRandom as pickRandomBoxes,
    applyLockEvent,
    applyDrawnEvent,
    pruneSelection,
} from './board-state.mjs';
import { revealMode, revealSteps } from './draw-reveal.mjs';

/** 可勾選的折抵種類。轉蛋券不在其中——一番賞完全不吃 */
const DISCOUNT_FLAGS = ['points', 'return_points'];

/** @param {{quote?: ?object}} [seed] @returns {object} */
export function initialState(seed = {}) {
    return {
        selection: [],
        submittedBoxes: [],
        quote: seed.quote ?? null,
        // 勾選狀態不跨次記憶：每次開窗都是全開
        discountFlags: { points: true, return_points: true },
        // 報價請求序號。晚到的舊報價靠它辨認並丟棄
        quoteSeq: 0,
        quoting: false,
        phase: 'idle',
        reveal: { mode: null, steps: [], index: 0, result: null },
        returnOutcome: null,
    };
}

function render(name, args = []) {
    return { type: 'render', name, args };
}

/**
 * @param {object} result 開獎結果 payload
 * @param {boolean} hasPeel
 * @returns {{mode: string, steps: Array, index: number, result: object}}
 */
function buildReveal(result, hasPeel) {
    const mode = revealMode(result, hasPeel);
    if (mode !== 'peel') {
        return { mode, steps: [], index: 0, result };
    }

    const steps = revealSteps(result);
    if (!steps.length) {
        return { mode: 'overview', steps: [], index: 0, result };
    }

    return { mode: 'peel', steps, index: 0, result };
}

function revealEnterEffects(reveal) {
    if (reveal.mode === 'peel') {
        return [render('startPeelReveal', [reveal.steps, reveal.result]), render('showPeelStep', [0])];
    }
    if (reveal.mode === 'group-buy') {
        return [render('renderGroupBuyPopup', [reveal.result])];
    }
    return [render('renderOverviewPopup', [reveal.result])];
}

/**
 * 這則報價回應是不是已經過期。沒帶序號的呼叫端（開窗那次、選號被剔除時的清空）
 * 一律視為當前，維持既有行為。
 * @param {object} state
 * @param {{seq?: number}} event
 * @returns {boolean}
 */
function isStaleQuote(state, event) {
    return event.seq !== undefined && event.seq !== state.quoteSeq;
}

function finishReveal(state) {
    const effects = state.reveal.mode === 'peel' ? [render('finishPeelReveal')] : [];
    return {
        state: { ...state, phase: 'idle', reveal: { mode: null, steps: [], index: 0, result: null } },
        effects,
    };
}

/**
 * @param {object} state
 * @param {object} event
 * @returns {{state: object, effects: Array<{type: string, [key: string]: *}>}}
 */
export function reduce(state, event) {
    switch (event.type) {
        case 'toggle': {
            const selection = toggleSelection(state.selection, event.boxNumber, event.boxes, event.nowMs);
            return { state: { ...state, selection }, effects: [render('renderBoard')] };
        }

        case 'selectAll': {
            const selection = selectAllAvailable(event.boxes, event.nowMs);
            return { state: { ...state, selection }, effects: [render('renderBoard')] };
        }

        case 'pickRandom': {
            const selection = pickRandomBoxes(event.boxes, event.count, event.nowMs, event.rand);
            return { state: { ...state, selection }, effects: [render('renderBoard')] };
        }

        case 'clearSelection':
            return { state: { ...state, selection: [] }, effects: [render('renderBoard')] };

        // 常駐盤面重抓（首次載入 / 續連後 refetch）：只重算選號的存活狀況，盤面本身
        // 已由呼叫端更新完畢
        case 'boxesReplaced': {
            const pruned = pruneSelection(state.selection, event.boxes, event.nowMs);
            const effects = [render('renderBoard')];
            if (pruned.removed.length) {
                effects.unshift(render('notifyRemoved', [pruned.removed]));
            }
            return { state: { ...state, selection: pruned.selection }, effects };
        }

        case 'boardLockEvent': {
            const boxes = applyLockEvent(event.boxes, {
                boxNumbers: event.boxNumbers,
                untilMs: event.untilMs,
                locked: event.locked,
            });
            const pruned = pruneSelection(state.selection, boxes, event.nowMs);
            const effects = [render('boardUpdated', [boxes]), render('renderBoard')];
            if (pruned.removed.length) {
                effects.unshift(render('notifyRemoved', [pruned.removed]));
            }
            return { state: { ...state, selection: pruned.selection }, effects };
        }

        case 'boardDrawnEvent': {
            const boxes = applyDrawnEvent(event.boxes, event.drawnBoxes);
            const pruned = pruneSelection(state.selection, boxes, event.nowMs);
            const effects = [render('boardUpdated', [boxes]), render('renderBoard')];
            if (pruned.removed.length) {
                effects.unshift(render('notifyRemoved', [pruned.removed]));
            }
            return { state: { ...state, selection: pruned.selection }, effects };
        }

        // 勾選變動：帶 checkbox 的**新值**而非翻轉——去抖動會把連續兩次壓成一次
        // dispatch，翻轉語意下 DOM 與 state 就會對不起來。金額一律由後端算，
        // 前端不複製一份拆分邏輯，兩邊才不會分岔
        case 'discountToggled': {
            if (!DISCOUNT_FLAGS.includes(event.flag) || typeof event.enabled !== 'boolean') {
                return { state, effects: [] };
            }
            const discountFlags = { ...state.discountFlags, [event.flag]: event.enabled };
            const quoteSeq = state.quoteSeq + 1;
            return {
                state: { ...state, discountFlags, quoteSeq, quoting: true },
                effects: [render('setSubmitDisabled', [true]), render('requestQuote', [discountFlags, quoteSeq])],
            };
        }

        case 'quoteLoaded': {
            if (isStaleQuote(state, event)) {
                return { state, effects: [] };
            }
            if (!event.quote) {
                return { state: { ...state, quote: null, quoting: false }, effects: [] };
            }
            // 旗標以後端實際採用的為準，前端的翻轉只是請求，不是結論
            const discountFlags = event.quote.discount_flags
                ? { ...state.discountFlags, ...event.quote.discount_flags }
                : state.discountFlags;
            return {
                state: { ...state, quote: event.quote, discountFlags, quoting: false },
                effects: [render('renderDrawPayPopup', [event.quote])],
            };
        }

        // 報價失敗：退回留存的那份報價並整份重繪。旗標一併回退——畫面顯示的是舊報價，
        // 旗標若停在翻過的值，送出的旗標與畫面金額就是兩回事。重繪也讓確認鈕重新走
        // drawButtonState()，同意條款與「未開放付現」兩道 gate 不會被繞過
        case 'quoteFailed': {
            if (isStaleQuote(state, event)) {
                return { state, effects: [] };
            }
            if (!state.quote) {
                return { state: { ...state, quoting: false }, effects: [] };
            }
            const discountFlags = state.quote.discount_flags
                ? { ...state.discountFlags, ...state.quote.discount_flags }
                : state.discountFlags;
            return {
                state: { ...state, discountFlags, quoting: false },
                effects: [render('renderDrawPayPopup', [state.quote])],
            };
        }

        case 'submitPointsOnly':
        case 'submitReserve':
        case 'submitCard': {
            if (state.phase !== 'idle') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'submitting', submittedBoxes: state.selection.slice() },
                effects: [render('setSubmitDisabled', [true])],
            };
        }

        // 送出後被拒（交易外／交易內擋下，或請求本身失敗）：收回 submitting，選號維持不動
        case 'submitBlocked': {
            if (state.phase !== 'submitting') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'idle', submittedBoxes: [] },
                effects: [render('setSubmitDisabled', [false]), render('closeDrawPayPopup')],
            };
        }

        // 跳轉付現成功 / 當頁刷卡需要 3D 驗證：頁面即將離開，不回 idle
        case 'redirecting': {
            if (state.phase !== 'submitting') {
                return { state, effects: [] };
            }
            return { state: { ...state, phase: 'returning' }, effects: [{ type: 'redirect', url: event.url }] };
        }

        // 當頁刷卡結果未定，同一頁內直接交回程輪詢收斂（無需離開頁面）。呼叫端只在
        // drawPollScheduler.start() 回 true（真的起了一輪輪詢）時才 dispatch 這個事件，
        // 所以這裡設回程訊息不會誤導——與 returnStart 對稱，兩個進入 returning 的入口
        // 行為一致，才吻合「三條回程路徑收斂到同一組事件」的設計目標。
        case 'returning': {
            if (state.phase !== 'submitting') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'returning', selection: [], submittedBoxes: [] },
                effects: [
                    render('setSubmitDisabled', [false]),
                    render('closeDrawPayPopup'),
                    render('setDrawReturnMessage', [event.message]),
                ],
            };
        }

        // 回程輪詢的另一個起點：跳轉付現／當頁刷卡 3D 驗證回導後的全新頁面載入，
        // 頁面狀態從 idle 直接進入 returning，與同頁 'returning' 事件收斂到同一組終局事件
        case 'returnStart': {
            if (state.phase !== 'idle') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'returning' },
                effects: [render('setDrawReturnMessage', [event.message])],
            };
        }

        // 開獎結果已確定（純點數立即成功 / 當頁刷卡立即成功 / 回程輪詢收斂為 done 三條路徑共用）
        case 'resultReady': {
            if (state.phase !== 'submitting') {
                return { state, effects: [] };
            }
            const reveal = buildReveal(event.result, event.hasPeel);
            return {
                state: { ...state, phase: 'revealing', selection: [], submittedBoxes: [], reveal, returnOutcome: 'done' },
                effects: [render('setSubmitDisabled', [false]), render('closeDrawPayPopup'), ...revealEnterEffects(reveal)],
            };
        }

        case 'revealNext': {
            if (state.phase !== 'revealing' || state.reveal.mode !== 'peel') {
                return { state, effects: [] };
            }
            const nextIndex = state.reveal.index + 1;
            if (nextIndex < state.reveal.steps.length) {
                return {
                    state: { ...state, reveal: { ...state.reveal, index: nextIndex } },
                    effects: [render('showPeelStep', [nextIndex])],
                };
            }
            return finishReveal(state);
        }

        case 'revealFinish':
            if (state.phase !== 'revealing') {
                return { state, effects: [] };
            }
            return finishReveal(state);

        // 金流回程：仍在等待（付款確認中／開獎中），非終局
        case 'returnTick':
            if (state.phase !== 'returning') {
                return { state, effects: [] };
            }
            return { state, effects: [render('setDrawReturnMessage', [event.message])] };

        // 金流回程三條路徑（跳轉付現回導 / 當頁刷卡回導 / 當頁刷卡輪詢）收斂到同一終局：成功
        case 'returnResolved': {
            if (state.phase !== 'returning') {
                return { state, effects: [] };
            }
            const reveal = buildReveal(event.result, event.hasPeel);
            return {
                state: { ...state, phase: 'revealing', selection: [], submittedBoxes: [], reveal, returnOutcome: 'done' },
                effects: [
                    render('setDrawReturnMessage', ['']),
                    render('clearDrawReturnState'),
                    ...revealEnterEffects(reveal),
                ],
            };
        }

        // 金流回程終局：後端明確判定失敗。彈窗留給使用者自行關閉才清狀態（重整頁面仍要能
        // 重現失敗文案），故只標記終局、不在此清 sessionStorage／URL。
        case 'returnFailed': {
            if (state.phase !== 'returning') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'settled', returnOutcome: 'failed' },
                effects: [render('setDrawReturnMessage', [event.message]), render('markReturnTerminal')],
            };
        }

        // 金流回程終局：逾硬性上限或傳輸持續失敗，視為逾時。無需使用者手動關閉即可清狀態。
        case 'returnTimeout': {
            if (state.phase !== 'returning') {
                return { state, effects: [] };
            }
            return {
                state: { ...state, phase: 'settled', returnOutcome: 'timeout' },
                effects: [render('setDrawReturnMessage', [event.message]), render('clearDrawReturnState')],
            };
        }

        // 使用者關閉回流狀態彈窗，把終局態收回 idle，讓下一輪抽獎的送出守衛
        // 真的放行——settled 本身沒有其他出口，缺這個事件會讓按鈕永久卡死
        case 'returnAcknowledged':
            if (state.phase !== 'settled') {
                return { state, effects: [] };
            }
            return { state: { ...state, phase: 'idle', returnOutcome: null }, effects: [] };

        default:
            return { state, effects: [] };
    }
}
